"use client";

/**
 * usePriceStream - Real-time price streaming from Polymarket
 *
 * Connects to the backend WebSocket server to receive real-time
 * price updates for specific market assets (tokenIds).
 *
 * Architecture:
 * - Frontend connects to backend WebSocket
 * - Frontend subscribes to `market:{assetId}` channels
 * - Backend maintains single connection to Polymarket
 * - Backend broadcasts price updates to all subscribed clients
 *
 * This enables real-time prices for thousands of users without
 * hammering Polymarket with individual requests.
 */

import { useEffect, useRef, useCallback, useState, useMemo } from "react";
import { io, Socket } from "socket.io-client";
import { getAccessToken } from "@privy-io/react-auth";
import { useAuth } from "./useAuth";

// Price data from backend
export interface PriceData {
  assetId: string;
  market: string;
  bestBid: number;
  bestAsk: number;
  midPrice: number;
  lastTradePrice?: number;
  lastTradeSize?: number;
  lastTradeSide?: "BUY" | "SELL";
  timestamp: number;
}

interface UsePriceStreamOptions {
  /** Asset IDs (token IDs) to subscribe to */
  assetIds: string[];
  /** Callback when price updates are received */
  onPriceUpdate?: (price: PriceData) => void;
  /** Whether to auto-connect (default: true) */
  enabled?: boolean;
}

interface UsePriceStreamReturn {
  /** Map of assetId -> current price data */
  prices: Map<string, PriceData>;
  /** Whether the socket is connected */
  isConnected: boolean;
  /** Connection error if any */
  error: string | null;
  /** Get price for a specific asset */
  getPrice: (assetId: string) => PriceData | undefined;
  /**
   * Force a Socket.IO reconnect cycle on the shared socket. Used by
   * the upstream silent-freeze watchdog in `useRealtimeMarketPrice`
   * — Polymarket's CLOB WS sometimes stops emitting events while the
   * connection (and ping/pong) stays open, so `isConnected` lies. The
   * watchdog notices no `price:update` arrived in 60s and triggers
   * this to wake the upstream subscription.
   */
  forceReconnect: () => void;
}

// Backend WebSocket URL
const getWebSocketUrl = () => {
  if (typeof window === "undefined") return "";
  const backendUrl =
    process.env.NEXT_PUBLIC_BACKEND_WS_URL ||
    (window.location.hostname === "localhost"
      ? "http://localhost:4000"
      : window.location.hostname.includes("staging")
        ? "https://staging-api.frenflow.com"
        : "https://api.frenflow.com");
  return backendUrl;
};

// Singleton socket connection (shared across all usePriceStream instances)
// Exported for usePortfolioSocket to reuse the same authenticated connection
let sharedSocket: Socket | null = null;
export function getSharedPriceSocket(): Socket | null { return sharedSocket; }
let sharedSocketRefCount = 0;
let isConnecting = false;
let connectionPromise: Promise<Socket | null> | null = null;
let disconnectTimer: NodeJS.Timeout | null = null;

/**
 * Get or create the shared socket connection (async).
 * Used by hooks that need the socket without going through usePriceStream
 * (e.g. usePredictRealtimePrice on Predict-only pages).
 */
export async function getOrCreateSharedSocket(): Promise<Socket | null> {
  if (sharedSocket?.connected) return sharedSocket;
  if (isConnecting && connectionPromise) return connectionPromise;

  isConnecting = true;
  connectionPromise = (async () => {
    const wsUrl = getWebSocketUrl();
    if (!wsUrl) {
      isConnecting = false;
      connectionPromise = null;
      return null;
    }

    // Retry the initial handshake. Socket.IO's built-in reconnection only
    // triggers after the first successful connect — if the very first
    // handshake fails (transient 400s observed right after backend
    // redeploys) the singleton stayed null and every caller would silently
    // fall back to REST polling forever. Retry 3 times with backoff.
    const MAX_ATTEMPTS = 3;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      if (attempt > 0) {
        await new Promise((r) => setTimeout(r, 1500 * attempt));
      }
      try {
        const socket = io(wsUrl, {
          path: "/ws",
          transports: ["websocket", "polling"],
          auth: async (cb) => { cb({}); },
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 10000,
        });

        // Re-join crypto/equity measurement rooms after any (re)connect.
        // This path has no other connect handler, and the crypto/equity
        // consumers acquire the socket through here — without this, room
        // membership would be lost on every reconnect and the occupancy
        // probe would undercount. (Measurement-grade; see priceRoomRefCount.)
        socket.on("connect", rejoinPriceRooms);

        await new Promise<void>((resolve, reject) => {
          socket.once("connect", () => resolve());
          socket.once("connect_error", (err) => reject(err));
          socket.connect();
        });

        sharedSocket = socket;
        isConnecting = false;
        connectionPromise = null;
        return socket;
      } catch {
        // try again
      }
    }

    isConnecting = false;
    connectionPromise = null;
    return null;
  })();

  return connectionPromise;
}

/**
 * Increment/decrement the shared socket ref count.
 * Hooks that use getOrCreateSharedSocket must call these to prevent
 * premature disconnection when usePriceStream cleans up.
 */
export function acquireSharedSocket(): void {
  if (disconnectTimer) {
    clearTimeout(disconnectTimer);
    disconnectTimer = null;
  }
  sharedSocketRefCount++;
}

export function releaseSharedSocket(): void {
  sharedSocketRefCount--;
  if (sharedSocketRefCount === 0 && sharedSocket) {
    disconnectTimer = setTimeout(() => {
      if (sharedSocketRefCount === 0 && sharedSocket) {
        sharedSocket.disconnect();
        sharedSocket = null;
      }
      disconnectTimer = null;
    }, 2000);
  }
}

// Track subscriptions across all hook instances
const subscriptionRefCount = new Map<string, number>();

// ── Price-room membership (crypto / equity broadcast rooms) ────────────────
// MEASUREMENT INSTRUMENT (Deploy 1, 2026-07-05). The backend still broadcasts
// `crypto:price`/`equity:price` GLOBALLY, so joining these rooms is a NO-OP
// for data delivery — no chart depends on the join to receive ticks. Its only
// job is to let the backend count how many concurrent sockets have a crypto/
// equity surface mounted (adapter.rooms('crypto').size vs total sockets, see
// the ws-room-probe), which decides whether scoping the broadcast to a room
// (sendToChannel) is worth the incident-2026-04-21 risk. Mirrors the proven
// `subscriptionRefCount` market pattern above.
//
// NOTE for Deploy 2: this reconnect-rejoin is measurement-grade, NOT the
// hardened layer. Because the backend emits globally, a missed re-join can't
// freeze a chart here — so passing charts do NOT validate rejoin correctness.
// Flipping the backend to sendToChannel MUST keep its own explicit reconnect
// validation gate (kill socket → confirm the socket re-joined the room).
const priceRoomRefCount = new Map<string, number>();

export function joinPriceRoom(room: "crypto" | "equity"): void {
  const n = priceRoomRefCount.get(room) ?? 0;
  priceRoomRefCount.set(room, n + 1);
  if (n === 0) sharedSocket?.emit("subscribe", [room]);
}

export function leavePriceRoom(room: "crypto" | "equity"): void {
  const n = priceRoomRefCount.get(room) ?? 1;
  const next = n - 1;
  if (next <= 0) {
    priceRoomRefCount.delete(room);
    sharedSocket?.emit("unsubscribe", [room]);
  } else {
    priceRoomRefCount.set(room, next);
  }
}

// Re-join every active price room after a (re)connect — Socket.IO room
// membership is per-connection and cleared server-side on reconnect.
function rejoinPriceRooms(): void {
  for (const [room, n] of priceRoomRefCount) {
    if (n > 0) sharedSocket?.emit("subscribe", [room]);
  }
}

// Global price cache (updated by WebSocket)
const priceCache = new Map<string, PriceData>();

// Event emitter for price updates
type PriceListener = (price: PriceData) => void;
const priceListeners = new Set<PriceListener>();

export function usePriceStream(
  options: UsePriceStreamOptions
): UsePriceStreamReturn {
  const { assetIds, onPriceUpdate, enabled = true } = options;

  // Auth is optional - prices are public
  const { isLoggedIn } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prices, setPrices] = useState<Map<string, PriceData>>(new Map());
  const subscribedIdsRef = useRef<Set<string>>(new Set());
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);

  // Memoize assetIds to prevent unnecessary re-subscriptions
  const assetIdsKey = useMemo(() => assetIds.sort().join(","), [assetIds]);

  // Fetch WebSocket token from backend (only if logged in)
  const fetchWsToken = useCallback(async (): Promise<string | null> => {
    // Only try to get token if user is logged in
    // Prices are public, so anonymous connections work fine
    if (!isLoggedIn) {
      return null;
    }

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) return null;

      const response = await fetch("/api/ws/token", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return data.token;
    } catch {
      return null;
    }
  }, [isLoggedIn]);

  // Get or create shared socket connection
  const getSharedSocket = useCallback(async (): Promise<Socket | null> => {
    // If already connected, return existing socket
    if (sharedSocket?.connected) {
      return sharedSocket;
    }

    // If connection in progress, wait for it
    if (isConnecting && connectionPromise) {
      return connectionPromise;
    }

    // Start new connection
    isConnecting = true;
    connectionPromise = (async () => {
      try {
        // Try to get auth token (optional - prices are public)
        const token = await fetchWsToken();

        const wsUrl = getWebSocketUrl();
        if (!wsUrl) return null;

        // Connect with or without auth (prices are public)
        // Use auth function so Socket.IO refreshes the token on each reconnection
        // (static tokens expire after 15min, causing "invalid token" on reconnect)
        const socket = io(wsUrl, {
          path: "/ws",
          transports: ["websocket", "polling"],
          auth: async (cb) => {
            const freshToken = await fetchWsToken();
            cb(freshToken ? { token: freshToken } : {});
          },
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 10000,
        });

        // Set up global event handlers
        socket.on("connect", () => {
          setIsConnected(true);
          setError(null);
          reconnectAttemptsRef.current = 0;

          // Re-subscribe to all tracked assets
          const allAssets = Array.from(subscriptionRefCount.keys());
          if (allAssets.length > 0) {
            socket.emit(
              "subscribe",
              allAssets.map((id) => `market:${id}`)
            );
          }
          // Re-join crypto/equity measurement rooms (per-connection membership).
          rejoinPriceRooms();
        });

        socket.on("disconnect", () => {
          setIsConnected(false);
        });

        socket.on("connect_error", (err) => {
          setError(err.message);
          setIsConnected(false);
        });

        socket.on("reconnect", (attemptNumber) => {
          setIsConnected(true);
          setError(null);
          reconnectAttemptsRef.current = 0;
        });

        socket.on("reconnect_failed", () => {
          setError("Connection lost. Please refresh the page.");
          setIsConnected(false);
        });

        // Handle price updates
        socket.on("price:update", (price: PriceData) => {
          // Update global cache
          priceCache.set(price.assetId, price);

          // Notify all listeners
          priceListeners.forEach((listener) => listener(price));
        });

        await new Promise<void>((resolve, reject) => {
          socket.once("connect", () => resolve());
          socket.once("connect_error", (err) => reject(err));
          socket.connect();
        });

        sharedSocket = socket;
        return socket;
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : "Connection failed";
        setError(errorMessage);
        return null;
      } finally {
        isConnecting = false;
        connectionPromise = null;
      }
    })();

    return connectionPromise;
  }, [fetchWsToken]);

  // Subscribe to specific assets
  // NOTE: Takes socket as parameter to avoid closure issues with sharedSocket
  const subscribeToAssets = useCallback(
    (ids: string[], socket?: Socket | null) => {
      const s = socket || sharedSocket;
      if (!s?.connected || ids.length === 0) return;

      const newSubscriptions: string[] = [];

      for (const id of ids) {
        const currentCount = subscriptionRefCount.get(id) || 0;
        subscriptionRefCount.set(id, currentCount + 1);

        // Only subscribe if this is the first reference
        if (currentCount === 0) {
          newSubscriptions.push(`market:${id}`);
        }
      }

      if (newSubscriptions.length > 0) {
        s.emit("subscribe", newSubscriptions);
      }
    },
    []
  );

  // Unsubscribe from specific assets
  const unsubscribeFromAssets = useCallback((ids: string[], socket?: Socket | null) => {
    const s = socket || sharedSocket;
    if (!s?.connected || ids.length === 0) return;

    const toUnsubscribe: string[] = [];

    for (const id of ids) {
      const currentCount = subscriptionRefCount.get(id) || 1;
      const newCount = currentCount - 1;

      if (newCount <= 0) {
        subscriptionRefCount.delete(id);
        toUnsubscribe.push(`market:${id}`);
      } else {
        subscriptionRefCount.set(id, newCount);
      }
    }

    if (toUnsubscribe.length > 0) {
      s.emit("unsubscribe", toUnsubscribe);
    }
  }, []);

  // Handle price updates for this hook instance — batched via RAF to reduce re-renders
  const pendingRef = useRef(new Map<string, PriceData>());
  const rafRef = useRef<number | null>(null);
  const assetIdSet = useMemo(() => new Set(assetIds), [assetIdsKey]);

  useEffect(() => {
    const listener: PriceListener = (price) => {
      if (assetIdSet.has(price.assetId)) {
        pendingRef.current.set(price.assetId, price);
        if (!rafRef.current) {
          rafRef.current = requestAnimationFrame(() => {
            const updates = pendingRef.current;
            pendingRef.current = new Map();
            rafRef.current = null;
            setPrices((prev) => {
              const next = new Map(prev);
              updates.forEach((p, id) => next.set(id, p));
              return next;
            });
          });
        }
        onPriceUpdate?.(price);
      }
    };

    priceListeners.add(listener);

    return () => {
      priceListeners.delete(listener);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [assetIdsKey, onPriceUpdate, assetIdSet]);

  // Connect and subscribe when enabled
  useEffect(() => {
    if (!enabled || assetIds.length === 0) {
      return;
    }

    let isMounted = true;

    const init = async () => {
      // Cancel any pending disconnect — new subscriber arrived
      if (disconnectTimer) {
        clearTimeout(disconnectTimer);
        disconnectTimer = null;
      }
      sharedSocketRefCount++;

      // Retry initial handshake. Socket.IO's built-in `reconnection: true`
      // only kicks in AFTER the first successful connect, so if the very
      // first handshake fails (seen in prod as transient 400s right after
      // a backend redeploy) the singleton stays null and the page sticks
      // on REST polling forever. Retry 3 times with backoff before
      // accepting defeat.
      let socket: Socket | null = null;
      const MAX_ATTEMPTS = 3;
      for (let attempt = 0; attempt < MAX_ATTEMPTS && isMounted; attempt++) {
        if (attempt > 0) {
          await new Promise((r) => setTimeout(r, 1500 * attempt));
          if (!isMounted) return;
        }
        socket = await getSharedSocket();
        if (socket) break;
      }
      if (!socket || !isMounted) return;

      // Subscribe to our assets - pass socket to avoid closure issues
      subscribeToAssets(assetIds, socket);
      subscribedIdsRef.current = new Set(assetIds);

      // Initialize prices from cache
      const initialPrices = new Map<string, PriceData>();
      for (const id of assetIds) {
        const cached = priceCache.get(id);
        if (cached) {
          initialPrices.set(id, cached);
        }
      }
      if (initialPrices.size > 0) {
        setPrices(initialPrices);
      }
    };

    init();

    return () => {
      isMounted = false;

      // Unsubscribe from our assets
      const subscribed = Array.from(subscribedIdsRef.current);
      if (subscribed.length > 0) {
        unsubscribeFromAssets(subscribed);
      }
      subscribedIdsRef.current.clear();

      // Decrease ref count and schedule disconnect if no more users
      // Use a 2s delay so new components mounting (e.g. league switch) can reuse the socket
      sharedSocketRefCount--;
      if (sharedSocketRefCount === 0 && sharedSocket) {
        disconnectTimer = setTimeout(() => {
          if (sharedSocketRefCount === 0 && sharedSocket) {
            sharedSocket.disconnect();
            sharedSocket = null;
          }
          disconnectTimer = null;
        }, 2000);
      }

      // Clear reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [
    enabled,
    assetIdsKey,
    getSharedSocket,
    subscribeToAssets,
    unsubscribeFromAssets,
  ]);

  // Handle asset ID changes (subscribe to new, unsubscribe from old)
  useEffect(() => {
    if (!sharedSocket?.connected) return;

    const currentIds = new Set(assetIds);
    const previousIds = subscribedIdsRef.current;

    // Find new subscriptions
    const toSubscribe = assetIds.filter((id) => !previousIds.has(id));
    // Find removed subscriptions
    const toUnsubscribe = Array.from(previousIds).filter(
      (id) => !currentIds.has(id)
    );

    if (toSubscribe.length > 0) {
      subscribeToAssets(toSubscribe);
    }
    if (toUnsubscribe.length > 0) {
      unsubscribeFromAssets(toUnsubscribe);
    }

    subscribedIdsRef.current = currentIds;
  }, [assetIdsKey, subscribeToAssets, unsubscribeFromAssets]);

  // Get price for a specific asset
  const getPrice = useCallback(
    (assetId: string): PriceData | undefined => {
      return prices.get(assetId);
    },
    [prices]
  );

  const forceReconnect = useCallback(() => {
    // Disconnect → connect on the shared singleton. Socket.IO's built-in
    // `reconnection: true` handles re-establishing the underlying
    // transport; the existing `socket.on('connect')` handler then
    // re-subscribes every tracked asset. Acts globally on every hook
    // instance because the socket is shared, which is what we want —
    // a silent freeze affects all subscriptions equally.
    if (sharedSocket) {
      try {
        sharedSocket.disconnect();
        sharedSocket.connect();
      } catch {
        // No-op: if disconnect throws, the next connect attempt will retry.
      }
    }
  }, []);

  return {
    prices,
    isConnected,
    error,
    getPrice,
    forceReconnect,
  };
}

export default usePriceStream;
